/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { LanguageValue, OrganizationDetailsSummary } from '../../shared/models';
import { PersonalSettings, UserWorkspaces } from '../../shared/models/account/account.model';

/** Defines the user details as retrieved from the API. */
export interface UserDetails {
  /** The back-end generated user UID. */
  uid: string;
  /** The user first name. */
  firstName: string;
  /** The user lastname. */
  lastName: string;
  /** The profile image of the user. */
  imageUrl: string;
  /** The user-registered email. */
  email: string;
  /** The roles assigned to the user. */
  roles: UserRole[];
  groups: UserAssignedGroup[];
  /** The company details for the user. */
  organization?: OrganizationDetailsSummary;
  /** The user education person targeted Id. */
  eptid: string;
  description?: LanguageValue[];
  workspaces?: UserWorkspaces[];
  userSettings?: PersonalSettings;
  onBoardingSubmitted?: boolean;
  recoveryEmail?: string;
  attributes?: UserDetailsAttributes;
  dashboards?: UserDashboard[];
}

export interface UserDetailsAttributes {
  company_uid: string[];
  company_name: string[];
}

export type UserDashboardType = 'ADMIN' | 'EMPLOYER' | 'REVIEWER';

/** A locked portal the user lands on instead of My Hub. Derived by the backend; a user may have several. */
export interface UserDashboard {
  type: UserDashboardType;
  pageLink: string;
  /** Display name for the switcher — the client name for employer portals. */
  label?: string;
}

export const ADMIN_DASHBOARD_BASE_URL = '/admin';
export const EMPLOYER_DASHBOARD_BASE_URL = '/employer';
export const REVIEWER_DASHBOARD_BASE_URL = '/reviewer';

/**
 * The base route a dashboard user is locked to — the first segment of `dashboard.pageLink`
 * (e.g. `/employer/06b32...` → `/employer`, `/admin/06b32...` → `/admin`). The trailing id
 * segment is intentionally ignored. Returns `null` for standard users (no dashboard).
 */
export function dashboardBaseUrl(dashboard?: UserDashboard): string | null {
  if (!dashboard?.pageLink) {
    return null;
  }
  const [, baseSegment] = dashboard.pageLink.split('/');
  return baseSegment ? `/${baseSegment}` : null;
}

/**
 * The id segment of `dashboard.pageLink` — e.g. `/employer/06b32uCWSoCHNzoB` → `06b32uCWSoCHNzoB`,
 * `/admin/06b32uCWSoCHNzoB` → `06b32uCWSoCHNzoB`. Returns `null` for standard users (no dashboard)
 * or when the link carries no id.
 */
export function dashboardId(dashboard?: UserDashboard): string | null {
  if (!dashboard?.pageLink) {
    return null;
  }
  const [, , idSegment] = dashboard.pageLink.split('/');
  return idSegment || null;
}

/**
 * The dashboard the user is currently in: the one remembered under `activeKey` (its `pageLink`) while the user still
 * has it, otherwise the first. `null` for standard users.
 */
export function resolveActiveDashboard(dashboards: UserDashboard[] | undefined, activeKey: string | null): UserDashboard | null {
  if (!dashboards?.length) {
    return null;
  }
  return dashboards.find((dashboard) => dashboard.pageLink === activeKey) ?? dashboards[0];
}

/** The dashboard to open under `baseUrl`: the active one when it lives there, otherwise the first that does. */
export function dashboardForPortal(
  dashboards: UserDashboard[] | undefined,
  activeKey: string | null,
  baseUrl: string,
): UserDashboard | null {
  const inPortal = (dashboards ?? []).filter((dashboard) => dashboardBaseUrl(dashboard) === baseUrl);
  return inPortal.find((dashboard) => dashboard.pageLink === activeKey) ?? inPortal[0] ?? null;
}

export interface UserDetailsSummary {
  uid: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
  email: string;
  roles: UserRole[];
  groups: UserAssignedGroup[];
  eptid: string;
  organization?: OrganizationDetailsSummary;
  description?: LanguageValue[];
  workspaces?: UserWorkspaces[];
  userSettings?: PersonalSettings;
  onBoardingSubmitted?: boolean;
  recoveryEmail?: string;
  orgClientAdminOf?: string[];
  dashboards?: UserDashboard[];
}

/** Defines on board data request. */
export interface OnBoardingDataRequest {
  course?: string;
  institutionName?: string;
  institutionSite?: string;
  institutionCity?: string;
  institutionCountry?: string;
  institutionPersonalEmail?: string;
  userCity?: string;
  userCountry?: string;
  startYear?: number;
  completionYear?: number;
  degreeYear?: number;
  degree?: string;
  typeOfUser?: 'university_student' | 'independent' | 'graduate' | 'college_student';
}

export interface UserRole {
  type: UserRoleType;
  _id: string;
  name: LanguageValue[];
  privileges: UserRolePrivilege[];
}

export interface UserAssignedGroup {
  groupUid: string;
  addedOn: Date;
}

export interface UserRolePrivilege {
  assigned: boolean;
  label: LanguageValue[];
  name: string;
  privilegeGroup: UserRoleType;
  _id: string;
}

export type UserRoleType = 'ACCOUNT_OWNER' | 'LEARNER' | 'INSTRUCTOR' | 'ADMIN' | 'SUPER_ADMIN' | string;

export type UserSession = {
  id: string;
  deviceKind: string;
  operatingSystem: string;
  browser: string;
  start: string; // datetime
};
